import { Link } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoMark, Wordmark } from './brand/Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { isEmbeddedApp } from '../lib/embedded';
import './ui/ui.css';

/** Browser tab title: "<page title> · PinGo" for string titles, else the localized app name. */
export function useDocumentTitle(title: ReactNode) {
  const { t } = useTranslation('common');
  const appName = t('appName');
  const text = typeof title === 'string' || typeof title === 'number' ? String(title) : '';
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previous = document.title;
    document.title = text ? `${text} · ${appName}` : appName;
    // pages without the shell (spectator, caddy, TV) must not keep a stale admin title
    return () => {
      document.title = previous;
    };
  }, [text, appName]);
}

/**
 * Standard page chrome (header with brand + language switcher). Full-screen modes (TV, caddy) may
 * skip it. The header wraps instead of overflowing on narrow screens (≤480px: the wordmark is
 * visually hidden and the title takes its own line), so the language switcher always stays in view.
 */
export function AppShell({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  useDocumentTitle(title);
  const embedded = isEmbeddedApp();
  const brand = (
    <>
      <LogoMark size={32} />
      <span className="ui-shell__wordmark">
        <span className="visually-hidden">{t('appName')}</span>
        <span aria-hidden="true" style={{ fontSize: 24 }}>
          <Wordmark />
        </span>
      </span>
    </>
  );
  return (
    <>
      <header className="ui-shell__header">
        {embedded ? (
          // inside the PinGo app the landing page must not open in the WebView (nav audit §4.3)
          <span className="ui-shell__brand" data-testid="shell-brand">
            {brand}
          </span>
        ) : (
          <Link to="/" className="ui-shell__brand" data-testid="shell-brand">
            {brand}
          </Link>
        )}
        {title && <strong className="ui-shell__title">{title}</strong>}
        <div className="ui-shell__actions">
          {actions}
          {!embedded && <LanguageSwitcher compact />}
        </div>
      </header>
      <main className="page">{children}</main>
    </>
  );
}
